import type { ExerciseTypeConfig, ExerciseDefinitionBase, ExerciseEvaluationResult } from './types';

export * from './types';

/**
 * Registry for exercise types.
 * Provides a centralized lookup for exercise type configurations.
 */
class ExerciseRegistryImpl {
  private configs = new Map<string, ExerciseTypeConfig<string, ExerciseDefinitionBase, unknown, { kind: string }>>();

  /**
   * Register an exercise type configuration.
   */
  register<T extends ExerciseTypeConfig<string, ExerciseDefinitionBase, unknown, { kind: string }>>(
    config: T
  ): void {
    if (this.configs.has(config.type)) {
      console.warn(`Exercise type "${config.type}" is already registered. Overwriting.`);
    }
    this.configs.set(config.type, config);
  }

  /**
   * Get the configuration for an exercise type.
   */
  get(type: string): ExerciseTypeConfig<string, ExerciseDefinitionBase, unknown, { kind: string }> | undefined {
    return this.configs.get(type);
  }

  /**
   * Check if an exercise type is registered.
   */
  has(type: string): boolean {
    return this.configs.has(type);
  }

  /**
   * Get all registered exercise types.
   */
  getTypes(): string[] {
    return Array.from(this.configs.keys());
  }

  /**
   * Get all auto-gradable exercise types.
   */
  getAutoGradableTypes(): string[] {
    return Array.from(this.configs.entries())
      .filter(([_, config]) => config.isAutoGradable)
      .map(([type]) => type);
  }

  /**
   * Check if an exercise type is auto-gradable.
   */
  isAutoGradable(type: string): boolean {
    const config = this.configs.get(type);
    return config?.isAutoGradable ?? false;
  }

  /**
   * Get the default max score for an exercise.
   */
  getDefaultMaxScore(definition: ExerciseDefinitionBase & { type: string }): number {
    const config = this.configs.get(definition.type);
    if (!config) {
      return 100; // Fallback for unknown types
    }
    return config.defaultMaxScore(definition);
  }

  /**
   * Evaluate an exercise submission.
   * Returns null if the exercise type is not auto-gradable or not found.
   */
  evaluate(
    definition: ExerciseDefinitionBase & { type: string },
    submission: unknown
  ): ExerciseEvaluationResult | null {
    const config = this.configs.get(definition.type);
    if (!config || !config.evaluate) {
      return null;
    }
    return config.evaluate(definition, submission);
  }

  /**
   * Hydrate exercise details from server response.
   */
  hydrateDetails(type: string, serverDetails: unknown): Record<string, unknown> {
    const config = this.configs.get(type);
    if (!config) {
      return {};
    }
    return config.hydrateDetails(serverDetails);
  }

  /**
   * Create a default exercise of the given type.
   */
  createDefault(type: string, order: number): Record<string, unknown> | null {
    const config = this.configs.get(type);
    if (!config) {
      return null;
    }
    return config.createDefault(order) as Record<string, unknown>;
  }
}

/**
 * Singleton instance of the exercise registry.
 */
export const exerciseRegistry = new ExerciseRegistryImpl();

/**
 * Type guard to check if an exercise definition is auto-gradable.
 */
export function isAutoGradableExercise(
  definition: ExerciseDefinitionBase & { type: string }
): boolean {
  return exerciseRegistry.isAutoGradable(definition.type);
}
